/**
 * La grilla de jornadas. §11: el héroe de la pantalla es esta tabla, y se
 * navega sin mouse — cada fila es focusable y Enter/Espacio abre el detalle.
 */

import { Loader2 } from 'lucide-react';

import type { JornadaDto } from '../../api';
import { EnvoltorioTabla, FilaVacia } from '../../components/Tabla';
import { aDdMmAaaa } from '../../lib/fechas';
import { claveDe, grupoDe, PENDIENTES } from './dominio';
import { SalidaAMarcar } from './SalidaAMarcar';

const COLUMNAS = 12;

export interface PropsTabla {
  readonly jornadas: readonly JornadaDto[];
  readonly seleccion: ReadonlySet<string>;
  readonly activa: string | null;
  readonly procesando: string | null;
  readonly onAlternar: (clave: string) => void;
  readonly onAlternarTodas: (marcar: boolean) => void;
  readonly onAbrirDetalle: (j: JornadaDto) => void;
  readonly onProcesar: (j: JornadaDto) => void;
}

export function TablaJornadas(props: PropsTabla): React.JSX.Element {
  const impactables = props.jornadas.filter((j) => j.admiteImpacto);
  const todasMarcadas =
    impactables.length > 0 && impactables.every((j) => props.seleccion.has(claveDe(j)));
  const pendientes = props.jornadas.filter((j) => PENDIENTES.includes(j.estado)).length;

  return (
    <EnvoltorioTabla
      pie={
        <>
          <span>
            <strong>{props.jornadas.length}</strong>
            {' jornada(s) en el rango'}
          </span>
          <span className="pendientes">
            {`${String(pendientes)} pendiente(s) de procesar`}
          </span>
        </>
      }
    >
      <table className="tabla tabla--jornadas">
        <thead>
          <tr>
            <th scope="col" className="col-check">
              <input
                type="checkbox"
                checked={todasMarcadas}
                disabled={impactables.length === 0}
                aria-label="Seleccionar todas las jornadas a procesar"
                onChange={(ev) => props.onAlternarTodas(ev.target.checked)}
              />
            </th>
            <th scope="col">Dni</th>
            {/* Nombre y no "Chofer": la fila puede ser un acompañante. */}
            <th scope="col">Nombre</th>
            <th scope="col" className="num">Fecha</th>
            <th scope="col">Estado</th>
            <th scope="col" className="num">Entrada</th>
            <th scope="col">Viaje</th>
            <th scope="col">Función</th>
            <th scope="col" className="num">Turno</th>
            <th scope="col" className="num">Salida a marcar</th>
            <th scope="col">A registrar</th>
            <th scope="col">Acción</th>
          </tr>
        </thead>
        <tbody>
          {props.jornadas.length === 0 ? (
            <FilaVacia columnas={COLUMNAS}>
              No hay jornadas para esos filtros. Probá otra fecha o quitá el filtro de estado.
            </FilaVacia>
          ) : (
            props.jornadas.map((j) => (
              <Fila
                key={claveDe(j)}
                jornada={j}
                seleccionada={props.seleccion.has(claveDe(j))}
                activa={props.activa === claveDe(j)}
                procesando={props.procesando === claveDe(j)}
                onAlternar={props.onAlternar}
                onAbrirDetalle={props.onAbrirDetalle}
                onProcesar={props.onProcesar}
              />
            ))
          )}
        </tbody>
      </table>
    </EnvoltorioTabla>
  );
}

interface PropsFila {
  readonly jornada: JornadaDto;
  readonly seleccionada: boolean;
  readonly activa: boolean;
  readonly procesando: boolean;
  readonly onAlternar: (clave: string) => void;
  readonly onAbrirDetalle: (j: JornadaDto) => void;
  readonly onProcesar: (j: JornadaDto) => void;
}

function Fila({
  jornada: j,
  seleccionada,
  activa,
  procesando,
  onAlternar,
  onAbrirDetalle,
  onProcesar,
}: PropsFila): React.JSX.Element {
  const clave = claveDe(j);

  return (
    <tr
      className={activa ? 'fila-activa' : undefined}
      data-grupo={grupoDe(j.estado)}
      tabIndex={0}
      onClick={() => onAbrirDetalle(j)}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          onAbrirDetalle(j);
        }
      }}
    >
      <td className="col-check">
        {j.admiteImpacto && (
          <input
            type="checkbox"
            checked={seleccionada}
            aria-label={`Seleccionar dni ${j.dni} del ${aDdMmAaaa(j.fechaOperativa)}`}
            onClick={(ev) => ev.stopPropagation()}
            onChange={() => onAlternar(clave)}
          />
        )}
      </td>
      <td className="num">{j.dni}</td>
      <td className="truncado nombre">{j.nombre ?? '—'}</td>
      <td className="num">{aDdMmAaaa(j.fechaOperativa)}</td>
      <td>
        <span className="pastilla" data-estado={j.estado}>
          {j.etiquetaEstado}
        </span>
      </td>
      <td className="num">{j.fichadaEntrada?.hora ?? '—'}</td>
      <td className="truncado" title={j.viaje?.descripcion ?? undefined}>
        {j.viaje ? j.viaje.identificadorViaje : 'sin viaje'}
      </td>
      <td>
        {j.viaje ? (
          <span className="chip" data-funcion={j.viaje.funcion}>
            {j.viaje.funcion === 'ACOMPANANTE' ? 'Acompañante' : 'Chofer'}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="num">{j.turno ? `${j.turno.horaInicio}–${j.turno.horaFin}` : '—'}</td>
      <td className="num">
        <SalidaAMarcar jornada={j} />
      </td>
      <td className="truncado">{j.etiquetasAcciones.join(' + ') || '—'}</td>
      <td>
        {j.admiteImpacto ? (
          <button
            type="button"
            disabled={procesando}
            onClick={(ev) => {
              ev.stopPropagation();
              onProcesar(j);
            }}
          >
            {procesando && <Loader2 className="girando" size={14} aria-hidden="true" />}
            {procesando ? 'Procesando…' : 'Procesar'}
          </button>
        ) : (
          <button
            type="button"
            className="plano"
            onClick={(ev) => {
              ev.stopPropagation();
              onAbrirDetalle(j);
            }}
          >
            Ver detalle
          </button>
        )}
      </td>
    </tr>
  );
}
